import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatNum, apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Loader2, Download, ClipboardList, CreditCard } from "lucide-react";

const fmtMoney = (n) => `S$${formatNum(n || 0, { decimals: 0 })}`;

const STATUS = {
  quote_requested: { label: "Quote requested", variant: "warning" },
  quoted: { label: "Quote ready", variant: "success" },
  awaiting_payment: { label: "Awaiting payment", variant: "warning" },
  purchased: { label: "Purchased", variant: "success" },
  active: { label: "Active", variant: "success" },
  scoping: { label: "Scoping", variant: "outline" },
  in_delivery: { label: "In delivery", variant: "outline" },
  delivered: { label: "Delivered", variant: "success" },
  cancelled: { label: "Cancelled", variant: "destructive" },
};

async function downloadPdf(url, filename) {
  const r = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = u; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 4000);
}

export default function MyServices({ canBuy }) {
  const [engagements, setEngagements] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/services/engagements").then((r) => setEngagements(r.data.engagements || [])).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const payEngagement = async (e) => {
    setBusy(e.id);
    try {
      const { data } = await api.post(`/services/engagements/${e.id}/pay`, { origin_url: window.location.origin });
      if (data.mode === "checkout" && data.url) { window.location.href = data.url; return; }
      toast.success("Managed service activated."); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(""); }
  };

  return (
    <div className="card-flat overflow-hidden" data-testid="engagements-list">
      {!engagements.length ? (
        <div className="py-16 text-center text-muted-foreground text-sm">
          <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />
          You haven't purchased any services yet. Browse the Services Catalogue to get started.
        </div>
      ) : engagements.map((e) => {
        const st = STATUS[e.status] || { label: e.status, variant: "outline" };
        return (
          <div key={e.id} className="flex items-center gap-3 p-4 border-b border-border/50 last:border-0" data-testid={`engagement-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{e.service_name}</div>
              <div className="text-[11px] text-muted-foreground">
                {e.sow_ref}{e.price_sgd ? ` · ${fmtMoney(e.price_sgd)}${e.billing_type === "recurring" ? "/" + (e.recurring_interval === "year" ? "yr" : e.recurring_interval === "quarter" ? "qtr" : "mo") : ""}` : e.buy_type === "quote" ? " · awaiting quote" : ""}
              </div>
            </div>
            <Badge variant={st.variant}>{st.label}</Badge>
            {e.status === "quoted" && canBuy && (
              <Button size="sm" variant="primary" disabled={busy === e.id} onClick={() => payEngagement(e)} data-testid={`approve-pay-${e.id}`}>
                {busy === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CreditCard className="h-3.5 w-3.5" /> Approve & pay</>}
              </Button>
            )}
            {e.sow_issued && (
              <Button size="sm" variant="outline" onClick={() => downloadPdf(`/services/engagements/${e.id}/sow.pdf`, `${e.sow_ref}.pdf`)} data-testid={`sow-${e.id}`}>
                <Download className="h-3.5 w-3.5" /> SoW
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
